import { createColumnHelper } from '@tanstack/react-table';
import { SeatingInfo } from 'src/components/common/SeatingInfo';
import { ConnectedEventInfo } from 'src/components/Events/ConnectedEventInfo';
import { EventDateDisplay } from 'src/components/Events/EventInfo';
import {
  SaleMarketplaceIdDisplay,
  SaleMarketplaceIdDisplayProps,
} from 'src/components/Sales/SaleMarketplaceIdDisplay';
import {
  COLUMN_DEFAULT_SIZE_BASE,
  COLUMN_DEFAULT_SIZE_BASE_L,
  COLUMN_DEFAULT_SIZE_L,
  COLUMN_DEFAULT_SIZE_LG,
  COLUMN_MAX_SIZE,
  COLUMN_MIN_SIZE,
} from 'src/contexts/ColumnResizingContext/ColumnResizingContext.types';
import { Content } from 'src/contexts/ContentContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { vars } from 'src/core/themes';
import { Stack, TooltipPopover } from 'src/core/ui';
import { CopyToClipboardV2 } from 'src/core/ui/CopyToClipboard/CopyToClipboardV2';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FormatOption } from 'src/modals/EditTableColumns';
import { RecentSalesTableColumnPicker } from 'src/navigations/Routes/Home/RecentSalesSection/RecentSalesTableColumnPicker';
import { IconsFill, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { SEATING_SORTING_FN_NAME } from 'src/tables/common/hooks/useSeatingColumnSorting';
import { CustomNumericCell } from 'src/tables/ListingTable/configs/CustomNumericCell';
import {
  SeatInfoContainer,
  TableShimmeringDiv,
} from 'src/tables/Table/Table.styled';
import { DateCell, InHandCell } from 'src/tables/Table/TableCells';
import { createUserTagColumn } from 'src/tables/TableV2/factories/userTagColumnFactory';
import { useContextBasedUsers } from 'src/tables/TableV2/factories/userTagColumnHooks';
import {
  RecentSalesTableColumnId,
  SalesTableColumnId,
  SalesTableFlattenedColumnId,
} from 'src/utils/columns/sales/salesColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID } from 'src/utils/constants/contentIdMaps';
import {
  compareEventsByDate,
  compareEventsByName,
} from 'src/utils/eventWithDataUtils';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { safeSortDateTime, sortMoney } from 'src/utils/tableUtils';
import { SectionType } from 'src/utils/types/sectionType';
import {
  DateTimeRange,
  Feature,
  ListingNote,
  PointOfSaleSaleStatus,
  Seating,
  UiMoney,
} from 'src/WebApiController';

import * as styles from '../../../navigations/Routes/Home/RecentSalesSection/RecentSalesSection.css';
import {
  SaleWithEvent,
  SHIMMERING_DIV_HEIGHT_SINGLE_SALE,
} from '../SalesTable.type';
import { MarketplaceInfoCell } from './MarketplaceInfoCell';
import { ProceedsCell } from './ProceedsCell';
import { SalesActionsCell } from './SalesActionsCell';
import { SALE_TABLE_COLUMNS_CONFIG } from './SaleTableColumnsConfig';
import { SALE_TABLE_FLATTENED_COLUMNS_CONFIG } from './SaleTableFlattenedColumnsConfig';
import { SoldQuantityTicketTypeCell } from './SoldQuantityTicketTypeCell';
const columnHelper = createColumnHelper<SaleWithEvent | null>();

export const defaultRecentSalesV2Columns = [
  RecentSalesTableColumnId.EventName,
  RecentSalesTableColumnId.EventDate,
  RecentSalesTableColumnId.OrderDate,
  SalesTableFlattenedColumnId.Venue,
  RecentSalesTableColumnId.Seating,
  RecentSalesTableColumnId.Marketplace,
  RecentSalesTableColumnId.SoldQuantityTicketType,
  RecentSalesTableColumnId.SaleId,
  RecentSalesTableColumnId.PurchasedBy,
  SalesTableColumnId.Status,
  RecentSalesTableColumnId.InHandDate,
  RecentSalesTableColumnId.Proceeds,
  RecentSalesTableColumnId.ProceedsPerTicket,
  RecentSalesTableColumnId.PandL,
  RecentSalesTableColumnId.Margin,
  RecentSalesTableColumnId.Actions,
];

export const availableRecentSalesV2Columns = [...defaultRecentSalesV2Columns];

export const RECENT_SALES_TABLE_COLUMNS_CONFIG = {
  // Same as SalesTableFlattenedColumnId.Event
  // Just hardcoded the precision to be event name only
  // When Recent Sales supports column picker, we can merge it back
  [RecentSalesTableColumnId.EventName]: columnHelper.accessor((data) => data, {
    id: RecentSalesTableColumnId.EventName,
    header: ({ column: { id } }) => (
      <TableHeaderCellDiv align="left">
        <Content
          id={
            RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
              id as RecentSalesTableColumnId
            ]
          }
        />
      </TableHeaderCellDiv>
    ),
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_L,
    minSize: COLUMN_MIN_SIZE,
    maxSize: COLUMN_MAX_SIZE,
    cell: function Cell({ getValue, table }) {
      const sale = getValue<SaleWithEvent>();

      if (!sale) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (!sale.event) {
        return null;
      }

      return (
        <TableCellDiv align="left" title={sale.event.name}>
          <ConnectedEventInfo
            showYear={true}
            showEventDateTbd
            event={sale.event}
            performer={sale.performer}
            venue={sale.venue}
            eventNameOnly={true}
            slimMode
            useShortName
            showEventStatus
          />
        </TableCellDiv>
      );
    },
    sortingFn: compareEventsByName,
  }),
  [RecentSalesTableColumnId.EventDate]: columnHelper.accessor(
    (data) => data?.event?.dates,
    {
      id: RecentSalesTableColumnId.EventDate,
      header: () => (
        <TableCellDiv align="left">
          <Content id={ContentId.EventDate} />
        </TableCellDiv>
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, row: { original }, column, table }) {
        const columnId = column.id;

        const value = getValue<DateTimeRange | undefined>();
        const {
          userDefinedPrecision = FormatOption.DateTime_MonthDD_Time_Day,
        } = useColumnUserSetting(columnId, SectionType.RecentSalesTable);

        if (!original || !original?.sale) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        const customPrecision =
          table.options.meta?.formatOptionOverrides?.[
            RecentSalesTableColumnId.EventDate
          ];

        const date = applyNumberFormatting({
          precision: customPrecision ?? userDefinedPrecision,
          inputNumber: value?.start,
          isDateTime: true,
        }) as string;

        return (
          <TableCellDiv align="left">
            {userDefinedPrecision === FormatOption.DateTime_MonthDD_Time ? (
              <EventDateDisplay
                eventDateString={value?.start ?? undefined}
                eventEndDateString={value?.end ?? undefined}
                showEventDateTbd={true}
                eventSubDateFormat={' - h:mm aaa, eee'}
                slimMode
                displayClassName={styles.rowDataText}
              />
            ) : (
              <div
                style={{
                  textTransform: 'none',
                }}
                className={styles.rowDataText}
              >
                {date}
              </div>
            )}
          </TableCellDiv>
        );
      },
      sortingFn: compareEventsByDate,
    }
  ),
  // Same as SalesTableColumnId.Seating
  // Just the styles are different
  // When Recent Sales supports column picker, we can merge it back
  [RecentSalesTableColumnId.Seating]: columnHelper.accessor(
    (data) =>
      data
        ? {
            seating: data.sale.seating,
            listingNotes: data.sale.seatTraits,
            isSeatSaver: data.sale.isSeatSaver,
            adCmpFlightId: data.sale.adCmpFlightId,
            status: data.sale.status,
          }
        : null,
    {
      id: RecentSalesTableColumnId.Seating,
      header: ({ column: { id } }) => (
        <TableHeaderCellDiv align="left">
          <Content
            id={
              RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
                id as RecentSalesTableColumnId
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_LG,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      meta: {
        styleOverrides: {
          paddingTop: vars.spacing.xxs,
        },
      },
      cell: function Cell({ getValue, row: { original } }) {
        const hasWasteSaleFeature = useUserHasFeature(Feature.WasteSale);

        const value = getValue<{
          seating: Seating;
          listingNotes: ListingNote[];
          isSeatSaver: boolean;
          adCmpFlightId?: number | null;
          status: PointOfSaleSaleStatus;
        } | null>();
        if (value == null) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        const { seating, adCmpFlightId, isSeatSaver, status } = value;
        const hasMarketplaceProblem =
          original?.sale?.hasMarketplaceProblem ?? false;
        return (
          <TableCellDiv
            align="left"
            style={{
              position:
                'absolute' /* this is to make the cell vertically center */,
            }}
          >
            <Stack justifyContent="spaceBetween" width="full">
              <SeatInfoContainer style={{ maxWidth: '100%', width: '100%' }}>
                <SeatingInfo
                  {...seating}
                  isSeatSaver={isSeatSaver}
                  adCampaignFlightId={adCmpFlightId}
                  justifyIconContent="spaceBetween"
                  isWasted={
                    hasWasteSaleFeature &&
                    status === PointOfSaleSaleStatus.Wasted
                  }
                  multiLineDisplay={true}
                  fontSize={vars.typography.fontSize.base}
                  labelFontSize={vars.typography.fontSize.xs}
                />
              </SeatInfoContainer>
              {hasMarketplaceProblem && (
                <div>
                  <TooltipPopover
                    variant="link"
                    triggerContent={
                      <WarningOutlineIcon
                        fill={IconsFill.textWarning}
                        withHoverEffect
                      />
                    }
                  >
                    <WarningMessage
                      message={
                        <Content
                          id={ContentId.SaleHasMarketplaceProblemHoverText}
                        />
                      }
                    />
                  </TooltipPopover>
                </div>
              )}
            </Stack>
          </TableCellDiv>
        );
      },
      sortingFn: SEATING_SORTING_FN_NAME,
    }
  ),
  [RecentSalesTableColumnId.Marketplace]: columnHelper.accessor(
    (data) => data?.sale?.mkp,
    {
      id: RecentSalesTableColumnId.Marketplace,
      header: ({ column: { id } }) => null,
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_BASE,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({
        getValue,
        column: { id: columnId },
        row: { original },
      }) {
        if (original == null) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        return <MarketplaceInfoCell sale={original} />;
      },
    }
  ),
  [RecentSalesTableColumnId.SoldQuantityTicketType]: columnHelper.accessor(
    (data) => data?.sale?.qtySold,
    {
      id: RecentSalesTableColumnId.SoldQuantityTicketType,
      header: ({ column: { id } }) => null,
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_BASE,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({
        getValue,
        column: { id: columnId },
        row: { original },
      }) {
        if (original == null) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        return <SoldQuantityTicketTypeCell sale={original} />;
      },
    }
  ),
  // Same as SalesTableColumnId.Sale
  // Just now showing marketplace
  // When Recent Sales supports column picker, we can merge it back
  [RecentSalesTableColumnId.SaleId]: columnHelper.accessor(
    (data) =>
      !data
        ? null
        : {
            marketplaceSaleId: data.sale.idOnMkp,
            saleId: data.sale.id,
            isExternal: Boolean(data.sale.idOnMkp),
            marketplace: data.sale.mkp,
          },
    {
      id: RecentSalesTableColumnId.SaleId,
      header: ({ column: { id } }) => (
        <TableHeaderCellDiv align="left">
          <Content
            id={
              RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
                id as RecentSalesTableColumnId
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_BASE_L,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: ({ getValue }) => {
        const value = getValue();
        if (value == null) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        const props = value as SaleMarketplaceIdDisplayProps;
        return (
          <TableCellDiv align="left" style={{ height: '100%' }}>
            <Stack
              gap="s"
              alignItems="center"
              width="full"
              height="full"
              style={{ wordBreak: 'break-all' }}
            >
              <CopyToClipboardV2
                textToCopy={props.marketplaceSaleId || props.saleId}
              >
                <SaleMarketplaceIdDisplay
                  {...props}
                  showMarketplace={false}
                  classNames={styles.rowDataText}
                />
              </CopyToClipboardV2>
            </Stack>
          </TableCellDiv>
        );
      },
    }
  ),
  [RecentSalesTableColumnId.Proceeds]: columnHelper.accessor(
    (data) => data?.sale?.ttlNetProcs,
    {
      id: RecentSalesTableColumnId.Proceeds,
      header: ({ column: { id } }) => (
        <TableHeaderCellDiv align="left">
          <Content
            id={
              RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
                id as RecentSalesTableColumnId
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_BASE,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, column: { id: columnId }, table }) {
        const value = getValue<UiMoney | null>();
        if (value == null) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        const displayValue =
          applyNumberFormatting({
            precision: FormatOption.Percent_RoundToTwo,
            inputNumber: value?.amt,
            currencyCode: value?.currency,
            currencyDecimalDigits: value?.dec,
          }) ?? undefined;

        return (
          <TableCellDiv align="right" className={styles.rowDataText}>
            <ProceedsCell displayValue={displayValue as string | undefined} />
          </TableCellDiv>
        );
      },
      sortingFn: sortMoney,
    }
  ),
  [RecentSalesTableColumnId.ProceedsPerTicket]: columnHelper.accessor(
    (data) => {
      const procs = data?.sale?.ttlNetProcs;
      const qty = data?.sale?.qtySold;
      if (procs == null || !qty) return null;
      return { ...procs, amt: procs.amt / qty } as UiMoney;
    },
    {
      id: RecentSalesTableColumnId.ProceedsPerTicket,
      header: ({ column: { id } }) => (
        <TableHeaderCellDiv align="left">
          <Content
            id={
              RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
                id as RecentSalesTableColumnId
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_BASE,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue }) {
        const value = getValue<UiMoney | null>();
        if (value == null) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        const displayValue =
          applyNumberFormatting({
            precision: FormatOption.Percent_RoundToTwo,
            inputNumber: value?.amt,
            currencyCode: value?.currency,
            currencyDecimalDigits: value?.dec,
          }) ?? undefined;

        return (
          <TableCellDiv align="right" className={styles.rowDataText}>
            <ProceedsCell displayValue={displayValue as string | undefined} />
          </TableCellDiv>
        );
      },
      sortingFn: sortMoney,
    }
  ),
  [RecentSalesTableColumnId.PandL]: columnHelper.accessor(
    (data) => data?.sale?.pandL,
    {
      id: RecentSalesTableColumnId.PandL,
      header: ({ column: { id } }) => (
        <TableHeaderCellDiv align="left">
          <Content
            id={
              RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
                id as RecentSalesTableColumnId
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_BASE_L,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({
        getValue,
        column: { id: columnId },
        row: { original },
        table,
      }) {
        const value = getValue<UiMoney | null>();

        return (
          <div className={styles.rowDataText}>
            <CustomNumericCell
              columnId={columnId}
              userDefinedPrecision={FormatOption.Precise}
              value={value?.amt ?? null}
              isLoading={original == null}
              shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
              sectionType={SectionType.Sales}
              currencyCode={value?.currency ?? undefined}
              colorScheme={value?.amt && value.amt < 0 ? 'loss' : 'profit'}
            />
          </div>
        );
      },
      sortingFn: sortMoney,
    }
  ),
  [RecentSalesTableColumnId.Margin]: columnHelper.accessor(
    (data) => data?.sale?.margin,
    {
      id: RecentSalesTableColumnId.Margin,
      header: ({ column: { id } }) => (
        <TableHeaderCellDiv align="left">
          <Content
            id={
              RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
                id as RecentSalesTableColumnId
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_BASE_L,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({
        getValue,
        column: { id: columnId },
        row: { original },
        table,
      }) {
        const value = getValue<number | null>();

        return (
          <div className={styles.rowDataText}>
            <CustomNumericCell
              columnId={columnId}
              userDefinedPrecision={FormatOption.Percent_RoundToTwo}
              value={value}
              isLoading={original == null}
              shimmeringDivHeight={SHIMMERING_DIV_HEIGHT_SINGLE_SALE}
              sectionType={SectionType.Sales}
              isPercent={true}
              colorScheme={value != null && value < 0 ? 'loss' : 'profit'}
            />
          </div>
        );
      },
      sortingFn: sortMoney,
    }
  ),
  [RecentSalesTableColumnId.PurchasedBy]:
    createUserTagColumn<SaleWithEvent | null>({
      id: RecentSalesTableColumnId.PurchasedBy,
      headerContentId:
        RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
          RecentSalesTableColumnId.PurchasedBy
        ],
      accessor: (data) => data?.sale?.owners?.buyerIds,
      useUserDisplayHook: useContextBasedUsers,
      size: COLUMN_DEFAULT_SIZE_LG,
      enableColumnFilter: false,
    }),
  [RecentSalesTableColumnId.Actions]: columnHelper.display({
    id: RecentSalesTableColumnId.Actions,
    enableSorting: false,
    enableResizing: true,
    header: function Cell() {
      return (
        <TableHeaderCellDiv align="right" showTooltip={false}>
          <RecentSalesTableColumnPicker />
        </TableHeaderCellDiv>
      );
    },
    enableColumnFilter: false,
    size: COLUMN_DEFAULT_SIZE_LG,
    minSize: COLUMN_DEFAULT_SIZE_LG,
    maxSize: COLUMN_MAX_SIZE,
    cell: ({ row: { original }, table }) => {
      const linkOnly =
        table.options.meta?.formatOptionOverrides?.[
          RecentSalesTableColumnId.Actions
        ] === FormatOption.Actions_LinkOnly;

      return !original ? (
        <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
      ) : (
        <SalesActionsCell
          linkOnly={linkOnly}
          saleId={original.sale.id}
          marketplaceSaleId={original.sale.idOnMkp}
          marketplace={original.sale.mkp}
          saleStatus={original.sale.status}
          actions={original.sale.actions}
          ticketType={original.sale.ticketType}
          isNoFulfill={original.sale.isNoFulfill}
        />
      );
    },
  }),
  [RecentSalesTableColumnId.InHandDate]: columnHelper.accessor(
    (data) => data?.sale.inHandAt,
    {
      id: RecentSalesTableColumnId.InHandDate,
      header: () => <TableHeaderCellDiv align="left">IH</TableHeaderCellDiv>,
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_BASE_L,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, row: { original } }) {
        const value = getValue<string | null>();

        if (!original?.sale) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }
        return (
          <TableCellDiv align="left" className={styles.rowDataText}>
            <InHandCell
              date={value ? new Date(value) : null}
              needsToFulfill={Boolean(
                original?.sale.status &&
                ![
                  PointOfSaleSaleStatus.ProcessingFulfillment,
                  PointOfSaleSaleStatus.Fulfilled,
                  PointOfSaleSaleStatus.CancelledHold,
                  PointOfSaleSaleStatus.Rejected,
                  PointOfSaleSaleStatus.PendingRejection,
                ].includes(original?.sale.status)
              )}
              handleFulfillmentState
            />
          </TableCellDiv>
        );
      },
      sortingFn: safeSortDateTime<SaleWithEvent>,
    }
  ),
  [RecentSalesTableColumnId.OrderDate]: columnHelper.accessor(
    (data) => data?.sale.saleDate,
    {
      id: RecentSalesTableColumnId.OrderDate,
      header: ({ column: { id } }) => (
        <TableHeaderCellDiv align="left">
          <Content
            id={
              RECENT_SALES_TABLE_COLUMN_ID_TO_CONTENT_ID[
                id as RecentSalesTableColumnId
              ]
            }
          />
        </TableHeaderCellDiv>
      ),
      enableColumnFilter: false,
      size: COLUMN_DEFAULT_SIZE_L,
      minSize: COLUMN_MIN_SIZE,
      maxSize: COLUMN_MAX_SIZE,
      cell: function Cell({ getValue, column: { id: columnId }, table }) {
        const value = getValue<string>();
        const { userDefinedPrecision = FormatOption.DateTime_MonthDD_Time } =
          useColumnUserSetting(columnId, SectionType.RecentSalesTable);

        if (value == null) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        return (
          <TableCellDiv align="left">
            <DateCell
              precision={userDefinedPrecision}
              date={value}
              align="left"
              enableUtcFallback
              hideRelativeTerms
              style={{ fontSize: vars.typography.fontSize.base }}
            />
          </TableCellDiv>
        );
      },
      sortingFn: safeSortDateTime<SaleWithEvent>,
    }
  ),
} as const;

export const MERGED_RECENT_SALES_TABLE_COLUMNS_CONFIG = Object.fromEntries(
  Object.entries({
    ...SALE_TABLE_COLUMNS_CONFIG,
    ...SALE_TABLE_FLATTENED_COLUMNS_CONFIG,
    ...RECENT_SALES_TABLE_COLUMNS_CONFIG,
  }).filter(([key]) =>
    availableRecentSalesV2Columns.includes(
      key as
        | RecentSalesTableColumnId
        | SalesTableFlattenedColumnId
        | SalesTableColumnId
    )
  )
);
